import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { User, Calendar, HelpCircle, Lock, Eye, EyeOff, ArrowLeft, Check, X, BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

import { API } from '../utils/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter username, 2: Verify security, 3: Reset password
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");
  
  const [formData, setFormData] = useState({
    username: "",
    date_of_birth: "",
    secret_question: "",
    secret_answer: "",
    new_password: "",
    confirm_password: "",
  });

  // Password strength indicators
  const passwordChecks = {
    length: formData.new_password.length >= 8,
    uppercase: /[A-Z]/.test(formData.new_password),
    lowercase: /[a-z]/.test(formData.new_password),
    number: /\d/.test(formData.new_password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password),
  };
  
  const passwordStrong = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = formData.new_password === formData.confirm_password && formData.confirm_password.length > 0;

  const handleGetQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password/question/${formData.username}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Username not found");
      }
      
      setFormData({ ...formData, secret_question: data.secret_question });
      setStep(2);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to find username");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          date_of_birth: formData.date_of_birth,
          secret_answer: formData.secret_answer,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Verification failed");
      }
      
      setResetToken(data.reset_token);
      toast.success("Verification successful!");
      setStep(3);
    } catch (error) {
      console.error("Verification error:", error);
      toast.error(error.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!passwordStrong) {
      toast.error("Please ensure your password meets all requirements");
      return;
    }
    
    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          new_password: formData.new_password,
          reset_token: resetToken,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Password reset failed");
      }
      
      toast.success("Password reset successfully!");
      navigate("/login");
    } catch (error) {
      console.error("Reset error:", error);
      toast.error(error.message || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-black" />
          </div>
          <span className="text-2xl font-bold text-white">LifeLedger</span>
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center">
              {step === 1 && "Forgot Password"}
              {step === 2 && "Verify Identity"}
              {step === 3 && "Reset Password"}
            </CardTitle>
            <p className="text-gray-400 text-center text-sm">
              {step === 1 && "Enter your username to begin"}
              {step === 2 && "Answer your security question"}
              {step === 3 && "Create a new password"}
            </p>
          </CardHeader>
          <CardContent>
            {/* Step 1: Enter Username */}
            {step === 1 && (
              <form onSubmit={handleGetQuestion} className="space-y-4">
                <div>
                  <Label className="text-gray-300">Username</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                      placeholder="Enter your username"
                      required
                      className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !formData.username}
                  className="w-full bg-green-500 hover:bg-green-400 text-black font-semibold rounded-full"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Continue"
                  )}
                </Button>
              </form>
            )}

            {/* Step 2: Verify Security */}
            {step === 2 && (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="p-3 bg-secondary rounded-lg mb-4">
                  <p className="text-sm text-gray-400">Security Question:</p>
                  <p className="text-white font-medium">{formData.secret_question}</p>
                </div>

                <div>
                  <Label className="text-gray-300">Your Answer</Label>
                  <div className="relative mt-1">
                    <HelpCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      value={formData.secret_answer}
                      onChange={(e) => setFormData({ ...formData, secret_answer: e.target.value })}
                      placeholder="Enter your answer"
                      required
                      className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Date of Birth</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      required
                      className="pl-10 bg-secondary border-border text-white"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-green-500 hover:bg-green-400 text-black font-semibold rounded-full"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 3: Reset Password */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                  <p className="text-green-400 text-sm text-center">
                    ✓ Identity verified for <span className="font-bold">{formData.username}</span>
                  </p>
                </div>

                <div>
                  <Label className="text-gray-300">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.new_password}
                      onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="pl-10 pr-10 bg-secondary border-border text-white placeholder:text-gray-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Password strength indicators */}
                  <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                    <div className={`flex items-center gap-1 ${passwordChecks.length ? 'text-green-400' : 'text-gray-500'}`}>
                      {passwordChecks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      8+ characters
                    </div>
                    <div className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-green-400' : 'text-gray-500'}`}>
                      {passwordChecks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Uppercase letter
                    </div>
                    <div className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-green-400' : 'text-gray-500'}`}>
                      {passwordChecks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Lowercase letter
                    </div>
                    <div className={`flex items-center gap-1 ${passwordChecks.number ? 'text-green-400' : 'text-gray-500'}`}>
                      {passwordChecks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Number
                    </div>
                    <div className={`flex items-center gap-1 ${passwordChecks.special ? 'text-green-400' : 'text-gray-500'}`}>
                      {passwordChecks.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Special character
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                    />
                    {formData.confirm_password && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {passwordsMatch ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <X className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !passwordStrong || !passwordsMatch}
                  className="w-full bg-green-500 hover:bg-green-400 text-black font-semibold rounded-full"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="text-green-400 hover:text-green-300 text-sm">
                <ArrowLeft className="w-4 h-4 inline mr-1" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
